import {
  Alert,
  AlertIcon,
  Box,
  HStack,
  Select,
  Spinner,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { translate } from '../../../i18n';
import { SportType } from '../../../models/court/dto/court.dto';
import { OrganizationSportGameCapacityOptionDto } from '../../../models/organization/dto/organization-sport-game-capacity-option.dto';
import { useGetOrganizationGameCapacityOptions } from '../../organization/hooks/useOrganizationGameCapacityOptions';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import ErrorState from '../../../components/states/ErrorState';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import GameCapacityOptionEditor from './GameCapacityOptionEditor';
import { MAX_BOOKING_CAPACITY } from '../../../utils/sport-capacity';

export const SPORT_OPTIONS = Object.values(SportType)
  .sort()
  .map(sport => ({ value: sport, label: sport }));

/**
 * What is wrong with an organization's capacity offering for one sport, from
 * the player's point of view. Both cases are silent today: the operator sees a
 * tidy list and has no way to tell that the app will pick something they did
 * not intend, or that a row they configured can never be booked.
 *
 * - **No default.** With several options and none flagged, the server falls
 *   back to the sport's legacy capacity if it happens to be offered, else the
 *   lowest — so the app pre-selects a capacity nobody chose.
 * - **Unbookable.** `resolveBookingCapacity` rejects anything outside
 *   [1, 20] on every money path, and the player-facing offering drops it, so
 *   the row is dead configuration. New options are now bounded at write time;
 *   rows saved before that bound existed are what this catches.
 */
export function findCapacityOfferingWarnings(
  options: OrganizationSportGameCapacityOptionDto[],
  { maxCapacity }: { maxCapacity: number },
): { kind: 'no-default' | 'unbookable'; capacities: number[] }[] {
  const warnings: { kind: 'no-default' | 'unbookable'; capacities: number[] }[] =
    [];

  const unbookable = options
    .filter(
      option =>
        !Number.isInteger(option.capacity) ||
        option.capacity < 1 ||
        option.capacity > maxCapacity,
    )
    .map(option => option.capacity);

  if (unbookable.length > 0) {
    warnings.push({ kind: 'unbookable', capacities: unbookable });
  }

  const bookable = options.filter(option => !unbookable.includes(option.capacity));
  if (bookable.length > 1 && !bookable.some(option => option.isDefault)) {
    warnings.push({
      kind: 'no-default',
      capacities: bookable.map(option => option.capacity).sort((a, b) => a - b),
    });
  }

  return warnings;
}

export default function GameCapacityOptionsList() {
  const { selectedOrganization } = useSelectedOrganization();
  const [selectedSport, setSelectedSport] = useState<SportType | ''>('');
  const sectionColor = useColorModeValue('gray.700', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const { data: capacityOptions, isInitialLoading, isError } = useGetOrganizationGameCapacityOptions(
    selectedOrganization?.id || '',
    selectedSport ? { sport: selectedSport as SportType } : undefined
  );

  /**
   * The same conversion as `GameDurationsList`, for the same reason (D62): the
   * `!selectedOrganization` early return this file carried was a correct but
   * store-derived restatement of the hook's `enabled: !!organizationId`, and a
   * gate restated in a second file stops covering the read silently.
   *
   * The live half is the error arm. It keyed off the raw `error` ahead of any
   * data check, so a failed background refetch replaced a capacity offering the
   * operator was reading — warnings and all — with an error card. `unreadable`
   * requires `!data` (R96-3).
   */
  const readState = classifyRead(isError, isInitialLoading, capacityOptions);

  const { groupedOptions, sortedSports } = useMemo(() => {
    const grouped: Partial<Record<SportType, OrganizationSportGameCapacityOptionDto[]>> =
      capacityOptions?.reduce((acc, option) => {
        if (!acc[option.sport]) {
          acc[option.sport] = [];
        }
        acc[option.sport]!.push(option);
        return acc;
      }, {} as Partial<Record<SportType, OrganizationSportGameCapacityOptionDto[]>>) || {};

    const sorted = (Object.keys(grouped) as SportType[])
      .filter(sport => grouped[sport] && grouped[sport]!.length > 0)
      .sort();

    // Pre-sort options within each group by capacity
    for (const sport of sorted) {
      grouped[sport]!.sort((a, b) => a.capacity - b.capacity);
    }

    return { groupedOptions: grouped, sortedSports: sorted };
  }, [capacityOptions]);

  if (readState === 'never-issued') {
    return <ScopePrompt gate="organization" />;
  }

  if (readState === 'loading') {
    return (
      <Box display="flex" justifyContent="center" p={8}>
        <Spinner size="lg" />
      </Box>
    );
  }

  if (readState === 'unreadable') {
    return <ErrorState message={translate('game_capacity_options.error_loading')} />;
  }

  return (
    <Box p={4}>
      <HStack mb={6} justify="flex-end" align="center">
        <Box minWidth="200px">
          <Select
            value={selectedSport}
            onChange={(e) => setSelectedSport(e.target.value as SportType | '')}
            placeholder={translate('game_capacity_options.filter_by_sport')}
          >
            {SPORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Box>
      </HStack>

      {!capacityOptions || capacityOptions.length === 0 ? (
        <Box p={8} textAlign="center">
          <Text color="gray.500" fontSize="lg">
            {translate('game_capacity_options.no_capacity_options')}
          </Text>
        </Box>
      ) : (
        <VStack spacing={6} align="stretch">
          {sortedSports.map((sport) => (
            <Box key={sport}>
              <Text
                fontSize="md"
                fontWeight="semibold"
                mb={3}
                color={sectionColor}
                borderBottom="1px"
                borderColor={borderColor}
                pb={2}
              >
                {sport}
              </Text>
              {findCapacityOfferingWarnings(groupedOptions[sport] || [], {
                maxCapacity: MAX_BOOKING_CAPACITY,
              }).map(warning => (
                <Alert
                  key={warning.kind}
                  status="warning"
                  borderRadius="md"
                  fontSize="sm"
                  mb={3}
                >
                  <AlertIcon />
                  {warning.kind === 'no-default'
                    ? translate('game_capacity_options.no_default_warning')
                    : translate('game_capacity_options.unbookable_warning')
                        .replace('{{capacities}}', warning.capacities.join(', '))
                        .replace('{{max}}', String(MAX_BOOKING_CAPACITY))}
                </Alert>
              ))}
              <VStack spacing={3} align="stretch">
                {(groupedOptions[sport] || []).map((option: OrganizationSportGameCapacityOptionDto) => (
                    <GameCapacityOptionEditor
                      key={option.id}
                      capacityOption={option}
                    />
                  ))}
              </VStack>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
}
